import {get, post, patch, del} from './http';
import {type DeliveryAction} from './delivery-actions';

export type HopperTargetType = 'webhook' | 'sheet' | 'youtube';

// Which of the three stored targets the dashboard is currently measuring against. Purely a view
// choice - the server stores all three numbers and has no notion of a selected period.
export type HopperTargetPeriod = 'daily' | 'weekly' | 'monthly';

// Mirrors PRI.Shared.Submissions.SubmissionFieldCatalog.Fields on the server, which is the validator
// of record - this list is for the column-picker's <datalist> only, not enforced client-side.
export const SHEET_COLUMN_OPTIONS = [
  'id',
  'content_id',
  'item_external_id',
  'handle',
  'source_url',
  'display_name',
  'submission_type',
  'vertical',
  'risk',
  'action_status',
  'created_by',
  'created_at',
  'sent_to_client_at',
  'language',
  'topic',
  'location',
  'core_risk',
  'platform',
  'affiliate',
  'latest_platform_status',
  'latest_reason_code',
  'restricted_regions',
  'is_channel_monetized',
  'age_restricted',
  'made_for_kids',
  'safe_for_minors',
  'content_warning',
  'verified',
  'is_family_safe',
  'status_changed_at',
  'last_status_check_at',
  'notes',
  'example_ids',
  'today'
] as const;

// The default a new sheet hopper's column editor is seeded with - the same columns/order as the CSV
// export.
export const DEFAULT_SHEET_COLUMNS = [
  'id',
  'content_id',
  'item_external_id',
  'handle',
  'source_url',
  'display_name',
  'submission_type',
  'vertical',
  'risk',
  'action_status',
  'created_by',
  'created_at',
  'sent_to_client_at',
  'language',
  'topic',
  'location',
  'core_risk'
];

// A different secondary reason for one sub-category of the hopper's policy violation: sub_category is the
// NAME of the classification-tree node directly under it (a submission stores node names, not ids).
export type YoutubeSecondaryReasonOverride = {
  sub_category: string;
  secondary_reason_id: string;
};

export type HopperConfig = {
  id: string;
  name: string;
  policy_violation: string;
  submission_type: 'Actor' | 'Content';
  schedule: string | null;
  next_run_at: string | null;
  target_type: HopperTargetType;
  // Null for analysts even when one is configured - the API only returns the URL to roles that can
  // fire a hopper, since it doubles as a delivery credential. Use is_deliverable to test for a
  // configured target.
  webhook_url: string | null;
  // Server-derived: this hopper resolves to a real delivery address - a webhook URL, or a
  // spreadsheet id for a sheet target. False means nothing can be delivered through it.
  is_deliverable: boolean;
  spreadsheet_id: string | null;
  sheet_tab: string | null;
  sheet_columns: string[];
  // Opt-in internal-sheet copy, written alongside every delivery whatever target_type is. Best effort:
  // never changes the submission's delivered/failed state.
  internal_sheet_enabled: boolean;
  internal_spreadsheet_id: string | null;
  internal_sheet_tab: string | null;
  internal_sheet_columns: string[];
  // Pioneer API (youtube) target only. The refresh token itself is write-only - never present on any
  // response, see HopperConfigResponse.YoutubeRefreshTokenSet server-side.
  youtube_refresh_token_set: boolean;
  youtube_reason_id: string | null;
  youtube_secondary_reason_id: string | null;
  youtube_abuse_type: string | null;
  youtube_comments: string | null;
  youtube_language: string | null;
  youtube_secondary_reason_overrides: YoutubeSecondaryReasonOverride[];
  last_fired_at: string | null;
  batch_size: number;
  daily_target: number;
  weekly_target: number;
  monthly_target: number;
  enabled: boolean;
  test_mode: boolean;
  test_simulated_status_code: number | null;
  created_by: string;
  created_at: string;
  modified_by: string | null;
  modified_at: string | null;
};

export type CreateHopperConfigPayload = {
  name: string;
  policy_violation: string;
  submission_type: 'Actor' | 'Content';
  schedule?: string | null;
  target_type?: HopperTargetType;
  webhook_url?: string | null;
  spreadsheet_id?: string | null;
  sheet_tab?: string | null;
  sheet_columns?: string[];
  internal_sheet_enabled?: boolean;
  internal_spreadsheet_id?: string | null;
  internal_sheet_tab?: string | null;
  internal_sheet_columns?: string[];
  youtube_refresh_token?: string | null;
  youtube_reason_id?: string | null;
  youtube_secondary_reason_id?: string | null;
  youtube_abuse_type?: string | null;
  youtube_comments?: string | null;
  youtube_language?: string | null;
  // Content hopper only. On an update null leaves the map alone, a list replaces it whole, [] clears it.
  youtube_secondary_reason_overrides?: YoutubeSecondaryReasonOverride[] | null;
  batch_size?: number;
  daily_target?: number;
  weekly_target?: number;
  monthly_target?: number;
  enabled?: boolean;
  test_mode?: boolean;
  test_simulated_status_code?: number | null;
};

export type UpdateHopperConfigPayload = {
  name?: string;
  policy_violation?: string;
  submission_type?: 'Actor' | 'Content';
  schedule?: string | null;
  clear_schedule?: boolean;
  target_type?: HopperTargetType;
  webhook_url?: string | null;
  clear_webhook_url?: boolean;
  spreadsheet_id?: string | null;
  sheet_tab?: string | null;
  sheet_columns?: string[];
  internal_sheet_enabled?: boolean;
  internal_spreadsheet_id?: string | null;
  internal_sheet_tab?: string | null;
  internal_sheet_columns?: string[];
  // Mirrors clear_webhook_url's convention rather than the sheet fields' "empty means clear" one - the
  // UI never has the current token value to diff against (see HopperConfig.youtube_refresh_token_set).
  youtube_refresh_token?: string | null;
  clear_youtube_refresh_token?: boolean;
  youtube_reason_id?: string | null;
  youtube_secondary_reason_id?: string | null;
  youtube_abuse_type?: string | null;
  youtube_comments?: string | null;
  youtube_language?: string | null;
  // Content hopper only. On an update null leaves the map alone, a list replaces it whole, [] clears it.
  youtube_secondary_reason_overrides?: YoutubeSecondaryReasonOverride[] | null;
  batch_size?: number;
  daily_target?: number;
  weekly_target?: number;
  monthly_target?: number;
  enabled?: boolean;
  test_mode?: boolean;
  test_simulated_status_code?: number | null;
  // Same convention as clear_webhook_url: a null test_simulated_status_code reads as "leave
  // unchanged" server-side (see HopperConfigsController.Update), so blanking the field in the modal
  // has to say so explicitly.
  clear_test_simulated_status_code?: boolean;
};

export type FireHopperResult = {
  sent: number;
  video_ids: string[];
};

// Every SubmissionFieldCatalog value from the most recent matching submission - lets the column
// picker show real data next to each field name instead of a bare key.
export type SubmissionFieldSample = {
  found: boolean;
  stream_id: string | null;
  values: Record<string, string>;
};

// One videoAbuseReportReasons.list entry, for the Pioneer API target's reason/secondary-reason
// dropdowns - see YoutubeAbuseReportReasonDto server-side.
export type YoutubeAbuseReportSecondaryReason = {
  id: string;
  label: string | null;
};

export type YoutubeAbuseReportReason = {
  id: string;
  label: string | null;
  secondary_reasons: YoutubeAbuseReportSecondaryReason[];
};

// What the dashboard's target cards read of a hopper. The API serves it for every vertical, not just
// the caller's granted ones, and it carries none of a hopper's delivery settings.
export type HopperDashboardSummary = Pick<
  HopperConfig,
  | 'id'
  | 'policy_violation'
  | 'submission_type'
  | 'schedule'
  | 'next_run_at'
  | 'is_deliverable'
  | 'batch_size'
  | 'daily_target'
  | 'weekly_target'
  | 'monthly_target'
  | 'enabled'
>;

export const hopperConfigsApi = {
  list: async () => get<HopperConfig[]>('/hopper-configs'),
  dashboard: async () => get<HopperDashboardSummary[]>('/hopper-configs/dashboard'),
  create: async (payload: CreateHopperConfigPayload) =>
    post<CreateHopperConfigPayload, HopperConfig>('/hopper-configs', payload),
  update: async (id: string, payload: UpdateHopperConfigPayload) =>
    patch<UpdateHopperConfigPayload, HopperConfig>(`/hopper-configs/${id}`, payload),
  fire: async (id: string, submissionIds?: string[]) =>
    post<{submission_ids?: string[]}, FireHopperResult>(
      `/hopper-configs/${id}/fire`,
      submissionIds ? {submission_ids: submissionIds} : {}
    ),
  delete: async (id: string) => del(`/hopper-configs/${id}`),
  deliveryActions: async (id: string) =>
    get<DeliveryAction[]>(`/hopper-configs/${id}/delivery-actions`),
  clearDeliveryActions: async (id: string) => del(`/hopper-configs/${id}/delivery-actions`),
  fieldSample: async (vertical: string, submissionType: string) =>
    get<SubmissionFieldSample>('/submissions/field-sample', {
      policy_violation: vertical,
      submission_type: submissionType
    }),
  youtubeReasons: async () =>
    get<{results: YoutubeAbuseReportReason[]}>('/hopper-configs/youtube-reasons'),
  sheetDeliveryAccount: async () =>
    get<{email: string | null}>('/hopper-configs/sheet-delivery-account')
};
